import {
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  Output,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationExtras,
  QueryParamsHandling,
  Router,
} from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantStyleEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant-style.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { ComponentVariantEnum } from '~/component/enums/variant';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';

export const NAVIGATION_ROUTING = new InjectionToken<string[]>(
  'Navigation routing'
);

@Component({
  selector: 'app-organizer-shared-navigation-buttons',
  templateUrl: './navigation-buttons.component.html',
  styleUrls: ['./navigation-buttons.component.scss'],
})
export class OrganizerSharedNavigationButtonsComponent {
  @Output() private readonly clickNext = new EventEmitter<void>();
  @Input() public hasNextEmit = false;
  @Input() public hasPreviousButton = true;
  @Input() public nextDisabled = true;
  @Input() public nextLabel?: string;
  @Input() public nextNavigationExtras: NavigationExtras = {};
  @Input() public nextQueryParametersHandling: QueryParamsHandling = '';
  @Input() public nextSubmit = false;
  @Input() public nextVariant = VtmnPlayButtonVariantEnum.PRIMARY;
  @Input() public previousNavigationExtras: NavigationExtras = {};
  @Input() public previousQueryParametersHandling: QueryParamsHandling = '';
  @Input() public previousUrl = '';
  @Input() public variant: ComponentVariantEnum;

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  constructor(
    @Inject(NAVIGATION_ROUTING)
    private readonly navigationRouting: RoutingInterface[],
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationService: NavigationService,
    private readonly redirectionService: RedirectionService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly router: Router
  ) {}

  private get positionInConfig(): number {
    const currentName = NavigationService.getCurrentName(this.activatedRoute);

    return this.navigationRouting.findIndex(
      (route) => route.routeName === currentName
    );
  }

  public get footerClass(): string {
    return (
      this.variant === ComponentVariantEnum.Light &&
      'organizer-shared-navigation-buttons--light'
    );
  }

  public get nextQueryParameters(): object {
    return this.nextNavigationExtras?.queryParams || {};
  }

  public get nextRouterLink(): string {
    if (
      this.positionInConfig > -1 &&
      this.positionInConfig < this.navigationRouting?.length - 1
    ) {
      const navigationExtras = this.nextNavigationExtras;
      navigationExtras.queryParams = {};

      return this.navigationService.getNextPath(
        this.navigationRouting,
        this.activatedRoute,
        navigationExtras
      );
    }

    return '/';
  }

  public get previousQueryParameters(): object {
    return this.previousNavigationExtras?.queryParams || {};
  }

  public get previousRouterLink(): string {
    if (this.previousUrl) {
      return this.previousUrl;
    }

    if (this.positionInConfig > 0) {
      const navigationExtras = this.previousNavigationExtras;
      navigationExtras.queryParams = {};

      return this.navigationService.getPreviousPath(
        this.navigationRouting,
        this.activatedRoute,
        navigationExtras
      );
    }

    return '/';
  }

  public get prevVariantStyle(): VtmnPlayButtonVariantStyleEnum {
    return this.variant === ComponentVariantEnum.Light
      ? VtmnPlayButtonVariantStyleEnum.INVERSE
      : VtmnPlayButtonVariantStyleEnum.REGULAR;
  }

  public get isPreventStep(): boolean {
    const pathnameLastElement = this.router.url.split('/').pop();
    const tutorialSteps = [
      this.localizeRouterService.translateRoute(
        'organizer.activity-creation.welcome'
      ),
      this.localizeRouterService.translateRoute(
        'organizer.activity-creation.advice'
      ),
      this.localizeRouterService.translateRoute(
        'organizer.activity-creation.steps'
      ),
      this.localizeRouterService.translateRoute(
        'organizer.activity-creation.steps-1'
      ),
    ];
    return tutorialSteps.some((s) => s.includes(pathnameLastElement));
  }

  public nextButtonClick(): void {
    this.clickNext.emit();
  }

  public skipTutorial(): void {
    this.redirectionService.redirect('/organizer.activity-creation.type');
  }
}
